// Expanded panel under a Center row — mirrors FrenchPrep's center drill-down:
// admin card, six stat pills, limits editor, feature toggles, extend / revoke,
// live-activity intel with a shared-signal alert, login timeline preview.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Pencil, Trash2, Ban, RotateCcw, Clock, ShieldAlert, Users, CheckCircle2, XCircle,
  UserRound, Copy, Mail, Phone, MapPin, MonitorSmartphone, RefreshCw, ChevronRight, Eye,
} from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const FEATURE_ROWS = [
  { key: 'custom_branding',        title: 'Custom Branding · Center Name',
    hint: "Lets the center admin replace the wordmark in the sidebar." },
  { key: 'provisioned_by_badge',   title: '"Provisioned by" Badge',
    hint: 'Sleek badge above "Switch to exam" in the sidebar. Center admin sets their own text.' },
  { key: 'student_feedback_inbox', title: 'Student feedback inbox',
    hint: 'Shows the "Submit feedback" entry in the student menu.' },
  { key: 'multi_language_ui',      title: 'Multi-language UI',
    hint: 'EN / 中文 / 한국어 / Español / العربية / فارسی switcher for center users.' },
];

const STAT_TILES = [
  { key: 'instructors',        label: 'Instructors',      tint: 'text-blue-700 bg-blue-50 border-blue-100' },
  { key: 'active_students',    label: 'Active Students',  tint: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
  { key: 'all_time_students',  label: 'All-Time',         tint: 'text-slate-700 bg-slate-50 border-slate-100' },
  { key: 'removed_students',   label: 'Removed',          tint: 'text-rose-700 bg-rose-50 border-rose-100' },
  { key: 'students_this_month',label: 'This Month',       tint: 'text-sky-700 bg-sky-50 border-sky-100' },
  { key: 'students_this_year', label: 'This Year',        tint: 'text-amber-700 bg-amber-50 border-amber-100' },
];

function Toggle({ on, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      role="switch" aria-checked={!!on}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        on ? 'bg-slate-900' : 'bg-slate-300',
      )}>
      <span className={cn(
        'inline-block h-5 w-5 transform rounded-full bg-white transition-transform',
        on ? 'translate-x-5' : 'translate-x-0.5',
      )} />
    </button>
  );
}

function Copyable({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" onClick={async () => {
      try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* ignore */ }
    }} className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:border-slate-400">
      <Copy className="h-3 w-3" />{copied ? 'copied' : 'copy'}
    </button>
  );
}

function timeAgo(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const diff = t - Date.now();
  const absS = Math.floor(Math.abs(diff) / 1000);
  const chunk = (s) => {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    if (s < 86400 * 30) return `${Math.floor(s / 86400)}d`;
    if (s < 86400 * 365) return `${Math.floor(s / 86400 / 30)}mo`;
    return `${Math.floor(s / 86400 / 365)}y`;
  };
  return diff >= 0 ? `in ${chunk(absS)}` : `${chunk(absS)} ago`;
}

export default function CenterDetail({ orgId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editingLimits, setEditingLimits] = useState(false);
  const [limitsForm, setLimitsForm] = useState({ max_students: '', max_instructors: '' });
  const [busy, setBusy] = useState(false);
  const [viewingAsId, setViewingAsId] = useState(null);
  const [studentQ, setStudentQ] = useState('');
  const { startImpersonation } = useApp();
  const navigate = useNavigate();

  const viewAsUser = async (targetUserId, label) => {
    setViewingAsId(targetUserId);
    try {
      await startImpersonation(orgId, targetUserId);
      toast.success(`Now viewing as ${label}.`);
      navigate('/org');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not enter view.');
    } finally { setViewingAsId(null); }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.adminOrgDetail(orgId);
      setData(res.data);
      setLimitsForm({
        max_students: res.data.limits?.max_students ?? '',
        max_instructors: res.data.limits?.max_instructors ?? '',
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load center detail.');
    }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  if (error) {
    return <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-4 text-sm text-rose-600">{error}</div>;
  }
  if (!data) {
    return <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-6 text-sm text-slate-500">Loading center…</div>;
  }

  const { org, admin, instructors, students = [], stats, limits, features, intel, admin_timeline, recent_signins } = data;
  const filteredStudents = studentQ.trim()
    ? students.filter((s) => (s.name || '').toLowerCase().includes(studentQ.toLowerCase()) || (s.email || '').toLowerCase().includes(studentQ.toLowerCase()))
    : students;

  const toggleFeature = async (key, next) => {
    try {
      const res = await api.adminSetFeatures(orgId, { [key]: next });
      setData((d) => ({ ...d, features: res.data.features }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save feature.'); }
  };

  const saveLimits = async () => {
    setBusy(true);
    try {
      await api.adminSetLimits(orgId, {
        max_students: limitsForm.max_students === '' ? 0 : Number(limitsForm.max_students),
        max_instructors: limitsForm.max_instructors === '' ? 0 : Number(limitsForm.max_instructors),
      });
      toast.success('Limits saved.');
      setEditingLimits(false);
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save.'); }
    setBusy(false);
  };

  const extend = async (days) => {
    setBusy(true);
    try {
      await api.adminExtendAccess(orgId, days);
      toast.success(`Access extended +${days}d.`);
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not extend.'); }
    setBusy(false);
  };

  const revoke = async () => {
    if (!window.confirm(`Revoke access for "${org.name}"?\nThe center admin, instructors, and students will be locked out until you restore access.`)) return;
    setBusy(true);
    try {
      await api.adminRevokeAccess(orgId);
      toast.success('Access revoked. Users will see the gentle "no longer available" message.');
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not revoke.'); }
    setBusy(false);
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.adminRestoreAccess(orgId);
      toast.success('Access restored.');
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not restore.'); }
    setBusy(false);
  };

  const deleteOrg = async () => {
    if (!window.confirm(`Permanently DELETE "${org.name}"?\nThis removes the org, all users, and their attempts. Use "Revoke" for a reversible pause.`)) return;
    setBusy(true);
    try {
      await api.adminDeleteOrg(orgId);
      toast.success('Organization deleted.');
      onChanged?.();
      onClose?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not delete.'); }
    setBusy(false);
  };

  const blockUser = async (u) => {
    if (!window.confirm(`Block ${u.name || u.email}?\nOn their next login they will see:\n"Access is currently unavailable for your account. Please contact your admin."`)) return;
    try {
      await api.adminBlockUser(u.user_id, 'platform_admin');
      toast.success('User blocked.');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not block.'); }
  };

  const unblockUser = async (u) => {
    try {
      await api.adminUnblockUser(u.user_id);
      toast.success('User unblocked.');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not unblock.'); }
  };

  return (
    <div className="border-t border-slate-200 bg-slate-50/40 px-6 py-6" data-testid={`center-detail-${orgId}`}>
      {/* Admin card */}
      {admin ? (
        <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="center-admin-card">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-full bg-indigo-100 text-lg font-semibold text-indigo-700">
              {(admin.name || admin.email || '?').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate font-display text-[15px] font-semibold text-slate-900">{admin.name || admin.email}</p>
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">Admin</span>
                {admin.blocked && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-800">Blocked</span>}
              </div>
              <p className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                <Mail className="h-3 w-3" />{admin.email}
              </p>
            </div>
            {admin.blocked ? (
              <button className="inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                onClick={() => unblockUser(admin)} data-testid="unblock-admin-btn">
                <ShieldCheck className="h-3.5 w-3.5" /> Unblock
              </button>
            ) : (
              <button className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300"
                onClick={() => blockUser(admin)} data-testid="block-admin-btn">
                <Ban className="h-3.5 w-3.5" /> Block
              </button>
            )}
          </div>
        </section>
      ) : (
        <p className="text-sm text-slate-500">No center admin assigned yet.</p>
      )}

      {/* Stat tiles */}
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="center-stats">
        {STAT_TILES.map(({ key, label, tint }) => (
          <div key={key} className={cn('rounded-lg border px-3 py-3 text-center', tint)} data-testid={`stat-${key}`}>
            <p className="font-display text-2xl font-extrabold tabular-nums">{stats[key] ?? 0}</p>
            <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider">{label}</p>
          </div>
        ))}
      </div>

      {/* Center Limits */}
      <section className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="center-limits">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-slate-700" />
            <p className="font-display text-sm font-semibold text-slate-900">Center Limits</p>
          </div>
          {!editingLimits && (
            <button className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setEditingLimits(true)} data-testid="edit-limits-btn">
              <Pencil className="h-3 w-3" /> Edit Limits
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          <b>Max students</b> is this center&apos;s activation cap. Every activated seat can trigger AI grading once per writing/speaking item — after that the student sees their submission + AI feedback locked in. That&apos;s how grading spend stays bounded.
        </p>
        {editingLimits ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div><label className="mono-stat block">Max students</label>
              <Input type="number" min={0} value={limitsForm.max_students}
                onChange={(e) => setLimitsForm((f) => ({ ...f, max_students: e.target.value }))}
                placeholder="0 = no limit" className="mt-1 h-9" data-testid="limits-max-students" /></div>
            <div><label className="mono-stat block">Max instructors</label>
              <Input type="number" min={0} value={limitsForm.max_instructors}
                onChange={(e) => setLimitsForm((f) => ({ ...f, max_instructors: e.target.value }))}
                placeholder="0 = no limit" className="mt-1 h-9" data-testid="limits-max-instructors" /></div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Button size="sm" className="cta" onClick={saveLimits} disabled={busy} data-testid="save-limits-btn">
                {busy ? 'Saving…' : 'Save limits'}
              </Button>
              <button className="text-sm text-slate-400 hover:text-slate-700" onClick={() => setEditingLimits(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="mt-2 grid gap-4 sm:grid-cols-2 text-sm">
            <div><span className="mono-stat">Max students</span>
              <p className="mt-0.5 text-slate-800">{limits?.max_students ? limits.max_students : <span className="text-slate-400">No limit</span>}</p></div>
            <div><span className="mono-stat">Max instructors</span>
              <p className="mt-0.5 text-slate-800">{limits?.max_instructors ? limits.max_instructors : <span className="text-slate-400">No limit</span>}</p></div>
          </div>
        )}
      </section>

      {/* Feature Access */}
      <section className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="center-features">
        <div className="flex items-center gap-2">
          <UserRound className="h-4 w-4 text-slate-700" />
          <p className="font-display text-sm font-semibold text-slate-900">Feature Access</p>
        </div>
        <div className="mt-3 divide-y divide-slate-100">
          {FEATURE_ROWS.map(({ key, title, hint }) => {
            const on = !!features[key];
            return (
              <div key={key} className="flex items-start justify-between gap-4 py-3">
                <div>
                  <p className="text-sm font-medium text-slate-800">{title}</p>
                  {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
                </div>
                <Toggle on={on} testId={`feature-${key}`}
                  onClick={() => toggleFeature(key, !on)} />
              </div>
            );
          })}
        </div>
      </section>

      {/* Intel · Live activity */}
      <section className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="center-intel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity /><p className="font-display text-sm font-semibold text-slate-900">Intel · Live activity</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-md border border-slate-200 px-3 py-2"><p className="mono-stat">24h logins</p><p className="mt-1 font-display text-xl font-extrabold tabular-nums">{intel.logins_24h}</p></div>
          <div className="rounded-md border border-slate-200 px-3 py-2"><p className="mono-stat">7d logins</p><p className="mt-1 font-display text-xl font-extrabold tabular-nums">{intel.logins_7d}</p></div>
          <div className="rounded-md border border-slate-200 px-3 py-2"><p className="mono-stat">30d logins</p><p className="mt-1 font-display text-xl font-extrabold tabular-nums">{intel.logins_30d}</p></div>
        </div>

        {intel.admin && (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/50 px-3 py-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-slate-700"><span className="mono-stat">Center admin</span> · Last login <span className="font-medium">{timeAgo(intel.admin.last_login)}</span> · <span className="font-medium">{intel.admin.login_count}</span> total logins</p>
              {intel.admin.last_device && (
                <span className="inline-flex items-center gap-1 rounded bg-white px-2 py-0.5 text-xs text-slate-600">
                  <MonitorSmartphone className="h-3 w-3" />{intel.admin.last_device} · {intel.admin.last_os} · {intel.admin.last_browser}
                </span>
              )}
            </div>
          </div>
        )}

        {intel.forwarding?.shared && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-3" data-testid="forwarding-signal">
            <p className="text-sm font-medium text-rose-700">Forwarding signal · Likely shared</p>
            <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
              <div><p className="mono-stat">IPs</p><p className="mt-0.5 font-display text-lg font-extrabold text-rose-900">{intel.forwarding.ips}</p></div>
              <div><p className="mono-stat">Devices</p><p className="mt-0.5 font-display text-lg font-extrabold text-rose-900">{intel.forwarding.fingerprints}</p></div>
              <div><p className="mono-stat">Cities</p><p className="mt-0.5 font-display text-lg font-extrabold text-rose-900">{intel.forwarding.cities || '—'}</p></div>
              <div><p className="mono-stat">Countries</p><p className="mt-0.5 font-display text-lg font-extrabold text-rose-900">{intel.forwarding.countries || '—'}</p></div>
            </div>
            <p className="mt-2 text-xs text-rose-700">Distinct IP count is high — credentials may be shared with a developer or another person.</p>
          </div>
        )}

        {intel.suspicious_signals > 0 && (
          <div className="mt-3 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-3" data-testid="suspicious-alert">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-800" />
              <p className="text-sm text-amber-900">
                <span className="font-medium">Suspicious activity · {intel.suspicious_signals} signal{intel.suspicious_signals === 1 ? '' : 's'}.</span>{' '}
                Review the admin&apos;s device history before extending access.
              </p>
            </div>
            {admin && !admin.blocked && (
              <button className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
                onClick={() => blockUser(admin)}>
                <Ban className="h-3.5 w-3.5" /> Block
              </button>
            )}
          </div>
        )}
      </section>

      {/* Instructors + students under this org (block per person) */}
      {instructors.length > 0 && (
        <section className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="center-instructors">
          <p className="font-display text-sm font-semibold text-slate-900">Instructors ({instructors.length})</p>
          <div className="mt-2 divide-y divide-slate-100 text-sm">
            {instructors.map((i) => (
              <div key={i.user_id} className="flex items-center gap-3 py-2">
                <div className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700">{(i.name || i.email || '?').slice(0, 1).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-800">{i.name || i.email}</p>
                  <p className="truncate text-xs text-slate-500">{i.email}</p>
                </div>
                {!i.blocked && (
                  <button
                    className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:border-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-50"
                    onClick={() => viewAsUser(i.user_id, i.name || i.email)}
                    disabled={viewingAsId === i.user_id}
                    data-testid={`view-as-instructor-${i.user_id}`}
                  >
                    <Eye className="h-3 w-3" /> {viewingAsId === i.user_id ? 'Entering…' : 'View as'}
                  </button>
                )}
                {i.blocked ? (
                  <button className="rounded border border-emerald-300 px-2 py-0.5 text-xs text-emerald-700 hover:bg-emerald-50"
                    onClick={() => unblockUser(i)}>Unblock</button>
                ) : (
                  <button className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:border-rose-300 hover:text-rose-700"
                    onClick={() => blockUser(i)}>Block</button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Students under this org (searchable + block per person) */}
      <section className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="center-students">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-display text-sm font-semibold text-slate-900">Students ({students.length})</p>
          {students.length > 0 && (
            <div className="relative">
              <Input value={studentQ} onChange={(e) => setStudentQ(e.target.value)}
                placeholder="Search by name or email" className="h-8 w-56" data-testid="student-search" />
            </div>
          )}
        </div>
        {students.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No students activated yet.</p>
        ) : filteredStudents.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No students match &ldquo;{studentQ}&rdquo;.</p>
        ) : (
          <div className="mt-2 max-h-72 divide-y divide-slate-100 overflow-y-auto text-sm">
            {filteredStudents.map((s) => (
              <div key={s.user_id} className="flex items-center gap-3 py-2" data-testid={`student-row-${s.user_id}`}>
                <div className="grid h-8 w-8 place-items-center rounded-full bg-emerald-50 text-xs font-semibold text-emerald-800">{(s.name || s.email || '?').slice(0, 1).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="truncate font-medium text-slate-800">{s.name || s.email}</p>
                    {s.blocked && <span className="rounded bg-rose-100 px-1.5 py-0 text-[10px] font-bold uppercase tracking-wider text-rose-800">Blocked</span>}
                  </div>
                  <p className="truncate text-xs text-slate-500">{s.email}
                    {s.last_login && <> · last login {timeAgo(s.last_login)}</>}
                  </p>
                </div>
                {s.blocked ? (
                  <button className="rounded border border-emerald-300 px-2 py-0.5 text-xs text-emerald-700 hover:bg-emerald-50"
                    onClick={() => unblockUser(s)} data-testid={`unblock-student-${s.user_id}`}>Unblock</button>
                ) : (
                  <button className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:border-rose-300 hover:text-rose-700"
                    onClick={() => blockUser(s)} data-testid={`block-student-${s.user_id}`}>Block</button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Login timeline (admin, last 30) */}
      {admin_timeline.length > 0 && (
        <section className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="center-timeline">
          <p className="font-display text-sm font-semibold text-slate-900">Login timeline ({admin_timeline.length})</p>
          <div className="mt-2 max-h-60 overflow-y-auto text-sm">
            {admin_timeline.map((e) => (
              <div key={e.event_id} className="flex items-center justify-between gap-3 border-b border-slate-100 py-2 last:border-b-0">
                <div>
                  <p className="text-slate-800">{new Date(e.at).toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{e.device} · {e.os} · {e.browser} · {e.ip}</p>
                </div>
                <span className="text-xs text-slate-400">{timeAgo(e.at)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Danger + expiry controls */}
      <section className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="center-actions">
        <p className="font-display text-sm font-semibold text-slate-900">Access controls</p>
        {org.kind === 'demo' && (
          <>
            <p className="mt-1 text-xs text-slate-500">
              {org.trial_expires_at
                ? <>Trial expires {new Date(org.trial_expires_at).toLocaleDateString()} &middot; {timeAgo(org.trial_expires_at)}.</>
                : 'No expiry set on this demo.'}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {[7, 14, 30, 60].map((d) => (
                <button key={d} onClick={() => extend(d)} disabled={busy}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-50"
                  data-testid={`extend-${d}d`}>
                  <Clock className="mr-1 inline h-3 w-3" /> +{d}d
                </button>
              ))}
            </div>
          </>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {org.status === 'revoked' ? (
            <button onClick={restore} disabled={busy}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
              data-testid="restore-btn">
              <RotateCcw className="h-3.5 w-3.5" /> Restore access
            </button>
          ) : (
            <button onClick={revoke} disabled={busy}
              className="inline-flex items-center gap-1 rounded-md border border-rose-300 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50"
              data-testid="revoke-btn">
              <XCircle className="h-3.5 w-3.5" /> Revoke access now
            </button>
          )}
          <button onClick={deleteOrg} disabled={busy}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-rose-300 hover:text-rose-700"
            data-testid="delete-org-btn">
            <Trash2 className="h-3.5 w-3.5" /> Delete organization
          </button>
        </div>
      </section>
    </div>
  );
}

// Tiny inline icon shim (matches file-local reference)
function Activity() {
  return <svg className="h-4 w-4 text-slate-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
}
